import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Form, Grid, Icon, Loader } from "semantic-ui-react";
import { addNewVendor, getVendorInfolist } from "../../api/BorrowerApi";
import { addMockNewVendor, getMockVendorInfolist } from "../../api/mock/MockBorrowerApi";
import { combineAddresses, handleAddressValue, handleErrorResponse, handleFormChange, validateEmails, validateObj } from "../../Helpers";
import { IErrorResponse, IProxyUser } from "../interfaces/Global";
import BCDashboard from "../ui/BCDashboard";
import BCFormInput from "../ui/BCFormInput";
import BCValidationMessage from "../ui/BCValidationMessage";
import "../../styles/borrower/vendor-form.scss";
import { GlobalConstants } from "../constants/GlobalConstants";
import { INewVendor, IVendorObj } from "../interfaces/Borrower";
import BCMessage from "../ui/BCMessage";
import BCButton from "../ui/BCButton";
import { FeatureFlags } from "../constants/LocalServerConstants";

interface IBWVendorForm {
  isGreaterThanMobile: boolean;
  isGreaterThanTablet: boolean;
  isSidebar: boolean;
  isMessage: boolean;
  setIsMessage: React.Dispatch<React.SetStateAction<boolean>>;
  errorResponse: IErrorResponse | undefined; 
  setErrorResponse: React.Dispatch<React.SetStateAction<IErrorResponse | undefined>>; 
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>; 
  vendorId: string,
  proxyUser: IProxyUser | undefined;
}

const BWVendorForm = ({
  isGreaterThanMobile,
  isGreaterThanTablet,
  isSidebar,
  isMessage,
  setIsMessage,
  errorResponse,
  setErrorResponse,
  setSuccessMessage,
  vendorId,
  proxyUser,
} : IBWVendorForm) => {
  const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
  const [isLoading, setIsLoading] = useState(true as boolean);
  const [vendorInfoData, setVendorInfoData] = useState([] as IVendorObj[]);
  const [invalidProps, setInvalidProps] = useState([] as any);
  const [newOrganization, setNewOrganization] = useState({} as INewVendor);

  const history = useHistory();
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleBackToVendor = useCallback(() => {
    history.push("/vendor");
  }, [history]);

  const handleAddVendor = async (newOrganization) => {
    setIsLoading(true);
    setInvalidProps([]);
    let notValidProperties = [] as string[];
    notValidProperties = validateObj(newOrganization, ["firstName", "lastName", "vendorEmail", "name", "physicalZipCode", "physicalState", "physicalCity", "physicalStreet"]);

    if (vendorInfoData && vendorInfoData.length > 0) {
      const foundOrganizationEmail = vendorInfoData.find(x => x.email === newOrganization.vendorEmail);
      if(foundOrganizationEmail) {
        notValidProperties.push("custom-organization-email");
      }
    }

    const notValidEmails = validateEmails(newOrganization, ["vendorEmail"]);
    if (notValidEmails && notValidEmails.length > 0 && notValidProperties.length === 0) {
      notValidProperties.push("custom-organization-invalid-email");
    }

    if (notValidProperties.length === 0) {
      if (newOrganization.physicalAddressLine2) {
        newOrganization.physicalStreet = combineAddresses(newOrganization.physicalStreet, newOrganization.physicalAddressLine2);
      }

      setIsLoading(true);
      await addNewVendorAsync(newOrganization);
      setIsLoading(false);
    }
    else {
      setTimeout(() => { 
        setInvalidProps(notValidProperties);
      }, 50);
    }

    setIsLoading(false);
  };

  const addNewVendorAsync = async (newOrganization) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await addMockNewVendor(newOrganization) : await addNewVendor(newOrganization, proxyUser && proxyUser.id);
      if (response && response.ok) {
        setIsMessage(false);
        setSuccessMessage("The vendor has been added.");
        setTimeout(() => { 
          handleBackToVendor();
          setSuccessMessage("");
        }, 5000);
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }
  };

  useEffect(() => {
    const getVendorInfolistAsync = async () => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockVendorInfolist() : await getVendorInfolist(proxyUser && proxyUser.id);
        if (response && response.ok) {
          const vendorInfolist = await response.json();
          if (vendorInfolist && vendorInfolist.length > 0 && vendorInfolist.findIndex(vendorInfolistItem => vendorInfolistItem.id === vendorId) > -1) {
            handleBackToVendor();
          }
          else {
            setVendorInfoData(vendorInfolist);
            newOrganization.id = vendorId;
            setNewOrganization({...newOrganization});
          }
        }
        else {
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
        }
      }
      catch (error) {
        handleError();
      }
    };

    setIsLoading(false);
    if (isFirstLoad) {
      getVendorInfolistAsync();
    }
  }, [vendorId, proxyUser, setIsLoading, isFirstLoad]);

  useEffect(() => {
    setIsFirstLoad(false);
  }, []);

  const OrganizationFormLayout = [
    [
      {
        title: <div className="breadcrumbs">
        <div className="breadcrumbs-home" onClick={() => handleBackToVendor()}><Icon name="handshake" />Vendor</div>
        <div> &gt; </div>
        <div className="breadcrumbs-current">Add Organization</div>
      </div>,
        size: 16,
        content:
          <Form className="primary-form" autoComplete="off">
            <Grid className="vendor-form-grid"> 
              <Grid.Row className="application-row-buttons">
                <BCButton labelPosition="left" iconName="add" className="vendor-form-button" handleOnClick={() => handleAddVendor(newOrganization)}>
                  Save
                </BCButton>
                <BCButton labelPosition="left" iconName="trash alternate outline" className="vendor-form-button" handleOnClick={() => handleBackToVendor()}>
                  Close
                </BCButton>
              </Grid.Row>
              <Grid.Row className="application-row">
                <Grid.Column width={isGreaterThanMobile ? 5 : 16}>
                  <BCFormInput
                    labelClass="vendor-form-label"
                    type="input"
                    label="First Name"
                    placeholder="John"
                    obj={newOrganization}
                    field="firstName"
                    inputType="text"
                    handleFormChange={(value, field) => handleFormChange(value, field, newOrganization, setNewOrganization)}
                    required
                  />
                  <BCValidationMessage invalidProps={invalidProps} prop="firstName" />
                </Grid.Column>
                <Grid.Column width={isGreaterThanMobile ? 5 : 16}>
                  <BCFormInput
                    labelClass="vendor-form-label"
                    type="input"
                    label="Last Name"
                    placeholder="Smith"
                    obj={newOrganization}
                    field="lastName"
                    inputType="text"
                    handleFormChange={(value, field) => handleFormChange(value, field, newOrganization, setNewOrganization)}
                    required
                  />
                  <BCValidationMessage invalidProps={invalidProps} prop="lastName" />
                </Grid.Column>
                <Grid.Column width={isGreaterThanMobile ? 6 : 16}>
                  <BCFormInput
                    labelClass="vendor-form-label"
                    type="input"
                    label="Email"
                    placeholder="john.smith@gmail.com"
                    obj={newOrganization}
                    field="vendorEmail"
                    inputType="text"
                    handleFormChange={(value, field) => handleFormChange(value, field, newOrganization, setNewOrganization)}
                    required
                  />
                  <BCValidationMessage invalidProps={invalidProps} prop="vendorEmail" />
                  <BCValidationMessage 
                    invalidProps={invalidProps} 
                    prop="custom-organization-invalid-email"
                    propText="This email is invalid."
                  />
                  <BCValidationMessage
                    invalidProps={invalidProps} 
                    prop="custom-organization-email"
                    propText="This email has been used."
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={isGreaterThanMobile ? 5 : 16}>
                  <BCFormInput
                    labelClass="vendor-form-label"
                    type="input"
                    label="Organization Name"
                    placeholder="Builders Capital"
                    obj={newOrganization}
                    field="name"
                    inputType="text"
                    handleFormChange={(value, field) => handleFormChange(value, field, newOrganization, setNewOrganization)}
                    required
                  />
                  <BCValidationMessage invalidProps={invalidProps} prop="name" />
                </Grid.Column>
                <Grid.Column width={isGreaterThanMobile ? 5 : 16}>
                  <BCFormInput
                    label="Address"
                    labelClass="vendor-form-label"
                    type={GlobalConstants.FormInputTypes.PLACES}
                    placeDetailsObject={newOrganization}
                    setPlaceDetailsObject={setNewOrganization}
                    placeholder="1234 Main Street, Seattle, WA 98052, USA"
                    defaultValue={handleAddressValue({
                      address: newOrganization.physicalStreet,
                      city: newOrganization.physicalCity,
                      state: newOrganization.physicalState,
                      zip: newOrganization.physicalZipCode
                    })}
                    placeFieldsNameOverrides={[
                      {
                        addressComponent: "address",
                        field: "physicalStreet"
                      },
                      {
                        addressComponent: "city",
                        field: "physicalCity"
                      },
                      {
                        addressComponent: "state",
                        field: "physicalState"
                      },
                      {
                        addressComponent: "county",
                        field: "physicalCounty"
                      },
                      {
                        addressComponent: "zip",
                        field: "physicalZipCode"
                      },
                    ]}
                    required
                  />
                  {invalidProps.includes("physicalZipCode") ? <BCValidationMessage 
                      invalidProps={invalidProps} 
                      prop="physicalZipCode"
                      propText="Zip code is missing."
                    /> : invalidProps.includes("physicalState") ? <BCValidationMessage 
                      invalidProps={invalidProps} 
                      prop="physicalState"
                      propText="State is missing"
                    /> : invalidProps.includes("physicalCity") ? <BCValidationMessage 
                      invalidProps={invalidProps} 
                      prop="physicalCity" 
                      propText="City is missing." 
                    /> : invalidProps.includes("physicalStreet") ? <BCValidationMessage 
                      invalidProps={invalidProps} 
                      prop="physicalStreet" 
                      propText="Street address is missing." /> : <React.Fragment></React.Fragment>
                  }
                </Grid.Column>
                <Grid.Column width={isGreaterThanMobile ? 6 : 16}>
                  <BCFormInput
                    labelClass="vendor-form-label"
                    type={GlobalConstants.FormInputTypes.INPUT}
                    inputKey="vendor"
                    label="Address Line 2"
                    placeholder="Apartment, suite, unit, building, floor, etc."
                    obj={newOrganization}
                    field="physicalAddressLine2"
                    inputType={GlobalConstants.InputTypes.STRING}
                    handleFormChange={(value, field) => handleFormChange(value, field, newOrganization, setNewOrganization)}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
      }
    ]
  ];

  return (
    <div className={(isGreaterThanTablet && isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
      {isLoading ? <Loader active inline="centered" size="big" /> : <React.Fragment>
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={isMessage}
          transitionOnMount
          styles="full-width"
          onDismiss={() => setIsMessage(false)}
          errorResponse={errorResponse}
        />
        <BCDashboard
          DashboardLayout={OrganizationFormLayout}
          isGreaterThanMobile={isGreaterThanMobile}
        />
      </React.Fragment>}
      {FeatureFlags.IS_DEBUG && <React.Fragment>
        <button onClick={() => console.log(newOrganization)}>newOrganization</button>
      </React.Fragment>}
    </div>
  );
};

export default BWVendorForm;